import _ from 'lodash';
import Mgrs from 'mgrs';
import afdrsFuelModels from '../config/afdrs_fuel_models.json';
import afdrsFuelTypes from '../config/afdrs_fuel_types.json';
import afdrsSubFuelTypes from '../config/afdrs_sub_fuel_types.json';
import fuelTypes from '../config/fuel_types.json';
import hazardValues from '../config/hazard_values.json';
import indicatorConfig from '../config/indicators.json';
import {convertKtsToKph} from './wind_utils';

const indicatorConfigPrefixMap = _.keyBy(indicatorConfig, 'prefix');

/**
 * Value from a WMS GetFeatureInfo JSON feature.properties object: GeoServer often returns
 * a single band as one property, or multiple keys where the layer config names the field.
 */
export const pickFeatureValue = function (layer, properties) {
  if (!layer || !properties || typeof properties !== 'object') {
    return undefined;
  }
  const dataKeys = Object.keys(properties);
  if (dataKeys.length === 1) {
    return properties[dataKeys[0]];
  }
  return properties[layer.property_name];
};

export const safeGetPointValue = function (popup_data, null_value) {
  if (popup_data.details) {
    const value = popup_data.details.value;

    // Large negative numbers usually represent null values.
    if (parseFloat(value) < -1000) {
      return null;
    }
    if (typeof null_value !== 'undefined' && value === null_value) {
      return null;
    }

    return value;
  }
  return null;
};

export const formatPointValue = function (value, layer_prefix, units, rounding_rule, null_value, value_names) {
  let formatted;
  if (value === null || (null_value !== undefined && value === null_value)) {
    formatted = 'N/A';
  } else if (layer_prefix === 'FUEL_TYPE') {
    const fuelType = fuelTypes[value];
    formatted = fuelType ? fuelType.name : `Unknown (${value})`;
  } else if (layer_prefix === 'AFDRS_FuelType_SFC' || layer_prefix === 'HP_AFDRS_fuel_type') {
    const fuelType = afdrsFuelTypes[value];
    formatted = fuelType ? fuelType.name : `Unknown (${value})`;
  } else if (layer_prefix === 'HP_AFDRS_sub_fuel_type_SFC') {
    const fuelType = afdrsSubFuelTypes[value];
    formatted = fuelType ? fuelType.name : `Unknown (${value})`;
  } else if (layer_prefix === 'HP_AFDRS_fuel_model') {
    const fuelType = afdrsFuelModels[value];
    formatted = fuelType ? fuelType.name : `Unknown (${value})`;
  } else if (value_names !== undefined && value_names.exact !== undefined) {
    formatted = value_names.exact[value] !== undefined ? value_names.exact[value] : value_names.else;
  } else if (value_names !== undefined && value_names.scale !== undefined) {
    const valid_names = _.keys(value_names.scale)
                         .sort((a, b) => Number(a) - Number(b))
                         .filter(k => Number(value) < Number(k));
    formatted = valid_names.length > 0 ? value_names.scale[valid_names[0]] : value_names.else;
  } else if (layer_prefix.startsWith('HZ_')) {
    const hazardValue = hazardValues[value];
    formatted = hazardValue ? hazardValue.name : value;
  } else if (units === 'minutes' && value > 1440) {
    formatted = '> 24 hrs';
  } else if (units === 'kts') {
    formatted = convertKtsToKph(value).toFixed(rounding_rule || 0) + ' km/h';
  } else {
    formatted = parseFloat(value).toFixed(rounding_rule || 0) + ' ' + units;
  }

  return formatted;
};

export const sortExtendedDataByDisplayName = function (extended_data) {
  return (extended_data.sort(function (a, b) {
    if (a.indicator.displayName < b.indicator.displayName) {
      return -1;
    }
    if (a.indicator.displayName > b.indicator.displayName) {
      return 1;
    }
    return 0;
  }));
};

export const calculateMgrs = function (lat, lng) {
  let mgrs = 'No set';
  if (lat && lng) {
    mgrs = Mgrs.forward([lng, lat], 3);
    if (mgrs) {
      mgrs = mgrs.substring(3);
      mgrs = mgrs.substring(0, 2) + ' ' + mgrs.substring(2, 5) + ' ' + mgrs.substring(5);
    }
  }
  return mgrs;
};

export const round = (value, numDigits) => {
  if (typeof value !== 'undefined' && value !== null) {
    return Number(value.toFixed(numDigits));
  }
  return undefined;
};

export const convertedUnits = (i) => {
  let units = i.units;
  if (!units) {
    return '';
  }
  // Knots gets converted to kph below.
  if (units === 'kts') {
    units = 'km/h';
  }
  return ' (' + units + ')';
};

export const emptyGraphMap = (config) => {
  const map = {};

  config.forEach(graph => {
    map[graph.id] = [];
  });

  return map;
};

export const extractTicks = (new_graph_data, graph_key) => {
  return new_graph_data[graph_key].filter(e => e.name.includes('12:00 AM')).map(e => e.name);
};

export const newDataPoint = (point_key, time) => {
  const sortIndex = new Date(time).getTime();
  return {
    name: point_key,
    time,
    sortIndex
  };
};

export const constructPointRequestUrl = function (base, source, lat, lng, time, prefixes) {
  // Documentation for this request:
  // http://docs.geoserver.org/stable/en/user/services/wms/reference.html
  const baseRequestParameters = 'SERVICE=WMS&VERSION=1.1.1&REQUEST=GetFeatureInfo&SRS=EPSG:4326&X=1&Y=1&WIDTH=2&HEIGHT=2&INFO_FORMAT=application/json&EXCEPTIONS=application/json';
  const formatted_time = new Date(time).toISOString();
  const query_layers = prefixes.join(',');
  const bBoxSize = 0.02;

  return base + source + baseRequestParameters +
         '&QUERY_LAYERS=' + query_layers +
         '&LAYERS=' + query_layers +
         '&BBOX=' + (lng - bBoxSize) + ',' + (lat - bBoxSize) + ',' + (lng + bBoxSize) + ',' + (lat + bBoxSize) +
         '&TIME=' + formatted_time +
         '&FEATURE_COUNT=' + prefixes.length;
};

export const convertValue = (key, value) => {
  const layer = indicatorConfigPrefixMap[key];
  if (layer && value !== null && value !== undefined) {
    const converted = layer.units === 'kts' ? convertKtsToKph(value) : value;
    return Number(converted.toFixed(layer.rounding_rule || 0));
  }
  return value;
};

export const minZero = (value) => {
  return Math.max(0, value);
};

export const componentToHex = (c) => {
  let hex = Number(c).toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

export const rgbToHex = (r, g, b) => {
  return componentToHex(r) + componentToHex(g) + componentToHex(b);
};



// WEBPACK FOOTER //
// ./src/utils/point_utils.js